import { useGetPatientsInViewQuery } from '~/store/services/providerDashboard';

import { formatDateToUTCString } from '~/lib/util/date.util';
import { PATIENT_VIEWS, PatientViewKey } from '../constants';
import { PatientTabData } from './ProviderDashboardMyPatientsCard';

// The upcomingDischarge tab key differs from the backend's view param.
const UPCOMING_DISCHARGES_VIEW = 'upcoming_discharges';

const useView = (
  providerId: string,
  view: string,
  extra?: { timezone: string; date: string },
) =>
  useGetPatientsInViewQuery(
    { providerId, view, ...extra },
    { skip: !providerId },
  );

interface ProviderPatientViews {
  patientsByTab: Partial<Record<PatientViewKey, PatientTabData>>;
  refetchAll: () => void;
  isRefreshing: boolean;
}

export const useProviderPatientViews = (
  providerId: string,
): ProviderPatientViews => {
  // The today view is bounded by the provider's own day, so it requires the
  // timezone (+ local date) query params — mirroring the My Day tile.
  const todayDate = formatDateToUTCString(new Date());
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const active = useView(providerId, PATIENT_VIEWS.active);
  const today = useView(providerId, PATIENT_VIEWS.today, {
    timezone,
    date: todayDate,
  });
  const newPatients = useView(providerId, PATIENT_VIEWS.new);
  const paused = useView(providerId, PATIENT_VIEWS.paused);
  const unpaused = useView(providerId, PATIENT_VIEWS.unpaused);
  const reassigned = useView(providerId, PATIENT_VIEWS.reassigned);
  const upcomingDischarge = useView(providerId, UPCOMING_DISCHARGES_VIEW);

  const results = {
    [PATIENT_VIEWS.active]: active,
    [PATIENT_VIEWS.today]: today,
    [PATIENT_VIEWS.new]: newPatients,
    [PATIENT_VIEWS.paused]: paused,
    [PATIENT_VIEWS.unpaused]: unpaused,
    [PATIENT_VIEWS.reassigned]: reassigned,
    [PATIENT_VIEWS.upcomingDischarge]: upcomingDischarge,
  };

  const patientsByTab = Object.fromEntries(
    Object.entries(results).map(([view, result]) => [
      view,
      {
        patients: result.data ?? [],
        isLoading: result.isFetching,
        isError: result.isError,
      },
    ]),
  ) as Partial<Record<PatientViewKey, PatientTabData>>;

  const refetchAll = () =>
    Object.values(results).forEach((result) => result.refetch());

  const isRefreshing = Object.values(results).some(
    (result) => result.isFetching,
  );

  return { patientsByTab, refetchAll, isRefreshing };
};
